import React, { useEffect, useState } from "react";
import { compressLogo } from "@/lib/compressLogo";
import api from "@/lib/api";
import { API } from "@/lib/api";
import MasterDataTab from "@/pages/MasterDataTab";
import RolesMatrix from "@/pages/RolesMatrix";
import PermissionGrid from "@/pages/PermissionGrid";
import UserManagement from "@/pages/UserManagement";
import ActivityTimeline from "@/pages/ActivityTimeline";
import UsageMetricsTab from "@/pages/settings/UsageMetricsTab";
import OpeningBalancesWizard from "@/pages/settings/OpeningBalancesWizard";
import FixedAssetsWizard from "@/pages/settings/FixedAssetsWizard";
import BankMemoryTab from "@/pages/settings/BankMemoryTab";
import MergeCcDialog from "@/pages/settings/MergeCcDialog";
import SplitCcDialog from "@/pages/settings/SplitCcDialog";
import DeleteCompanySection from "@/pages/settings/DeleteCompanySection";
import CloneCompanySection from "@/pages/settings/CloneCompanySection";
import ChequeBookTab from "@/pages/settings/ChequeBookTab";
import WorkflowConsoleTab from "@/pages/settings/WorkflowConsoleTab";
import HRSyncPanel from "@/pages/HRSyncPanel";
import SmtpSettingsPanel from "@/pages/settings/SmtpSettingsPanel";
import EmailSignaturePanel from "@/pages/EmailSignaturePanel";
import EmailOutboxPanel from "@/pages/EmailOutboxPanel";
import ComplianceTab from "@/pages/settings/ComplianceTab";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { INDUSTRY_TYPES, isManufacturingIndustry } from "@/lib/industry-types";
import VoucherNumbering from "@/pages/settings/VoucherNumbering";
import GstConfig from "@/pages/settings/GstConfig";
import BrochureDesigner from "@/pages/settings/BrochureDesigner";
import ApprovalRulesTab from "@/pages/settings/ApprovalRulesTab";
import DepartmentsTab from "@/pages/settings/DepartmentsTab";
import DimensionBulkUpload from "@/components/DimensionBulkUpload";
import BusinessSegmentsSettingsTab from "@/pages/settings/BusinessSegmentsSettingsTab";  // noqa: retained for compat, no longer rendered
import DocumentTypesTab from "@/pages/settings/DocumentTypesTab";
import HelpArticleEditor from "@/pages/settings/HelpArticleEditor";
import TrashRestore from "@/pages/settings/TrashRestore";
import Referrals from "@/pages/settings/Referrals";
import BackupTab from "@/pages/settings/Backup";
import PasswordTab from "@/pages/settings/PasswordTab";
import PartnersTab from "@/pages/settings/PartnersTab";
import ESignSignatoriesTab from "@/pages/settings/ESignSignatoriesTab";
import GstBridgePanel from "@/pages/settings/GstBridgePanel";
import ShareholdersTab from "@/pages/settings/ShareholdersTab";
import StatementFormatEditor from "@/pages/settings/StatementFormatEditor";
import PageHeader from "@/components/common/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtDate, today } from "@/lib/format";
import { Lock, Unlock, Trash2, ChevronRight } from "lucide-react";
import { Switch } from "@/components/ui/switch";

function HierarchyList({ rows, onDelete, renderName }) {
  // Build a parent -> children map
  const byParent = rows.reduce((acc, r) => { const k = r.parent_id || "_root"; (acc[k] ||= []).push(r); return acc; }, {});
  const render = (node, depth = 0) => (
    <div key={node.id}>
      <div className="flex items-center gap-2 py-1.5" style={{ paddingLeft: depth * 20 }} data-testid={`hier-${node.id}`}>
        {depth > 0 && <ChevronRight className="w-3 h-3 text-muted-foreground" />}
        <div className="flex-1">{renderName(node)}</div>
        <button className="text-muted-foreground hover:text-destructive" onClick={() => onDelete(node)} data-testid={`delete-${node.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {(byParent[node.id] || []).map((c) => render(c, depth + 1))}
    </div>
  );
  return <div className="text-sm">{(byParent._root || []).map((n) => render(n))}</div>;
}

const MONTHS = [
  { v: 1, l: "January" }, { v: 2, l: "February" }, { v: 3, l: "March" },
  { v: 4, l: "April (Standard for India)" }, { v: 5, l: "May" }, { v: 6, l: "June" },
  { v: 7, l: "July" }, { v: 8, l: "August" }, { v: 9, l: "September" },
  { v: 10, l: "October" }, { v: 11, l: "November" }, { v: 12, l: "December" },
];

function CompanyProfileTab() {
  const [org, setOrg] = useState(null);
  const [currentRole, setCurrentRole] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await api.get("/auth/me");
      setOrg(r.data.org || {});
      setCurrentRole(r.data?.user?.role || r.data?.role || "");
    })();
  }, []);

  const set = (patch) => setOrg((o) => ({ ...o, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        name: org.name, entity_type: org.entity_type,
        gstin: (org.gstin || "").toUpperCase(),
        pan: (org.pan || "").toUpperCase(),
        cin: org.cin, state: org.state, country: org.country || "India",
        pincode: org.pincode, email: org.email, phone: org.phone,
        address: org.address, base_currency: org.base_currency || "INR",
        website: org.website,
        fiscal_year_start_month: Number(org.fiscal_year_start_month || 4),
      };
      await api.patch("/organization", patch);
      toast.success("Company profile updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  if (!org) return <div className="text-muted-foreground p-6">Loading…</div>;

  const gstinValid = /^\d{2}[A-Z0-9]{10}[0-9A-Z]{3}$/.test(org.gstin || "");
  const currentFY = (() => {
    const m = Number(org.fiscal_year_start_month || 4);
    const today = new Date();
    const y = today.getMonth() + 1 >= m ? today.getFullYear() : today.getFullYear() - 1;
    return `${y}-${String(y + 1).slice(2)}`;
  })();

  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="company-profile-tab">
      <div className="card-sharp p-5 md:col-span-2 space-y-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Legal / Statutory</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Company Name</Label><Input value={org.name || ""} onChange={(e) => set({ name: e.target.value })} className="mt-1" data-testid="org-name" /></div>
          <div><Label>Entity Type</Label>
            <Select value={org.entity_type || "private_limited"} onValueChange={(v) => set({ entity_type: v })}>
              <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="proprietorship">Proprietorship</SelectItem>
                <SelectItem value="partnership">Partnership</SelectItem>
                <SelectItem value="llp">LLP</SelectItem>
                <SelectItem value="private_limited">Private Limited</SelectItem>
                <SelectItem value="public_limited">Public Limited</SelectItem>
                <SelectItem value="ngo">NGO / Trust</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>GSTIN</Label>
            <div className="flex gap-1 mt-1">
              <Input value={org.gstin || ""} onChange={(e) => set({ gstin: e.target.value.toUpperCase() })} className="font-mono" placeholder="15-char" data-testid="org-gstin" />
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!org.gstin || (org.gstin || "").length !== 15}
                onClick={async () => {
                  try {
                    const r = await api.get(`/gstin/lookup`, { params: { gstin: org.gstin } });
                    if (!r.data.valid) { toast.error(r.data.error || "Invalid GSTIN"); return; }
                    set({
                      name: r.data.trade_name || r.data.legal_name || org.name,
                      pan: r.data.pan || org.pan,
                      state: r.data.state || org.state,
                      address: r.data.address || org.address,
                    });
                    toast.success("✓ Fetched from gst.gov.in");
                  } catch (e) { toast.error(e?.response?.data?.detail || "Fetch failed"); }
                }}
                className="rounded-sm whitespace-nowrap"
                data-testid="org-gstin-fetch"
              >⤓ Fetch</Button>
            </div>
            {org.gstin && !gstinValid && <div className="text-[10px] text-orange-500 mt-1">GSTIN format looks wrong (expect 15 chars, e.g. 29AAAAA0000A1Z5)</div>}
          </div>
          <div><Label>PAN</Label><Input value={org.pan || ""} onChange={(e) => set({ pan: e.target.value.toUpperCase() })} className="mt-1 font-mono" placeholder="10-char" data-testid="org-pan" /></div>
          <div><Label>CIN</Label><Input value={org.cin || ""} onChange={(e) => set({ cin: e.target.value.toUpperCase() })} className="mt-1 font-mono" data-testid="org-cin" /></div>
          <div><Label>State</Label><Input value={org.state || ""} onChange={(e) => set({ state: e.target.value })} className="mt-1" data-testid="org-state" /></div>
        </div>

        <div className="text-[10px] font-mono uppercase tracking-widest text-accent pt-3 border-t border-border">Contact</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Email</Label><Input value={org.email || ""} onChange={(e) => set({ email: e.target.value })} className="mt-1" /></div>
          <div><Label>Phone</Label><Input value={org.phone || ""} onChange={(e) => set({ phone: e.target.value })} className="mt-1" /></div>
          <div className="md:col-span-2"><Label>Address</Label><Input value={org.address || ""} onChange={(e) => set({ address: e.target.value })} className="mt-1" /></div>
          <div><Label>Pincode</Label><Input value={org.pincode || ""} onChange={(e) => set({ pincode: e.target.value })} className="mt-1 font-mono" /></div>
          <div><Label>Website</Label><Input value={org.website || ""} onChange={(e) => set({ website: e.target.value })} className="mt-1" /></div>
        </div>

        <Button onClick={save} disabled={saving} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="org-save-btn">
          {saving ? "Saving…" : "Save Company Profile"}
        </Button>

        <div className="border-t pt-4 mt-4">
          <AddonModulesSection org={org} set={set} />
        </div>

        <div className="border-t pt-4 mt-4">
          <BrandingSection org={org} set={set} />
        </div>

        <DeleteCompanySection org={org} currentRole={currentRole}
                               onOrgPatch={(patch) => setOrg((o) => ({ ...o, ...patch }))} />
        <CloneCompanySection org={org} currentRole={currentRole}
                               switchOrg={async () => window.location.assign("/app/settings")} />
      </div>

      <div className="card-sharp p-5 space-y-4" data-testid="fy-panel">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Financial Year</div>
        <div>
          <Label>FY Start Month</Label>
          <Select value={String(org.fiscal_year_start_month || 4)} onValueChange={(v) => set({ fiscal_year_start_month: Number(v) })}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="fy-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>)}</SelectContent>
          </Select>
          <div className="text-xs text-muted-foreground mt-2">
            India standard is <b>April → March</b>. Once set, all reports, GST filings, and Schedule III statements default to this FY.
          </div>
        </div>
        <div className="border-t border-border pt-3">
          <div className="text-[10px] font-mono uppercase text-muted-foreground">Current FY</div>
          <div className="text-xl font-mono mt-1" data-testid="current-fy">FY {currentFY}</div>
        </div>
        <div className="border-t border-border pt-3">
          <div className="text-[10px] font-mono uppercase text-muted-foreground">Base Currency</div>
          <div className="text-xl font-mono mt-1">{org.base_currency || "INR"}</div>
        </div>
      </div>
    </div>
  );
}

// Iter-144 · Retroactive Add-on Modules — Dealership + Manufacturing toggles
// so existing orgs can unlock these without re-signup. Backend PATCH
// /organization re-expands org.modules the moment either flips.
function AddonModulesSection({ org, set }) {
  const [busy, setBusy] = React.useState(false);
  const [expandedModules, setExpandedModules] = React.useState(null);
  // Iter-145 · Sidebar auto-refresh — pull subscription.refresh() so the
  // sidebar's `useSubscription().hasModule()` re-evaluates the moment we PATCH.
  const { refresh: refreshSubscription } = useSubscription();
  const { patchOrgLocal } = useAuth();
  // Iter-146 · Shared source of truth for the 6 canonical industries — keeps
  // Settings, Signup and OrgSwitcher perfectly aligned. Backend key contract
  // lives in `backend/seed.py::INDUSTRY_EXCLUDES`.
  const INDUSTRIES = INDUSTRY_TYPES.map(({ v, l }) => ({ v, l }));

  const patchOrg = async (patch, label) => {
    setBusy(true);
    try {
      const r = await api.patch("/organization", patch);
      set(patch);
      const mods = r.data?.modules || [];
      setExpandedModules(mods);
      // Iter-145 · Instant sidebar refresh — update the org in AuthContext
      // (so any child reading auth.org.modules sees new modules) and
      // re-fetch /api/subscription so the sidebar's hasModule() re-runs.
      try { patchOrgLocal({ ...patch, modules: mods }); } catch {}
      try { await refreshSubscription(); } catch {}
      toast.success(`${label} — sidebar refreshed (${mods.length} modules active).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const isMfg = isManufacturingIndustry(org.industry_type);

  return (
    <div className="space-y-4" data-testid="addon-modules-section">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Add-on modules · Enable / disable retroactively</div>
        <div className="text-[11px] text-muted-foreground mt-1">
          Legacy tenants can toggle these on/off any time. Modules refresh instantly — the sidebar updates the moment you flip a toggle.
        </div>
      </div>

      {/* Industry Type — drives Manufacturing add-on */}
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <Label>Industry Type</Label>
          <Select
            value={org.industry_type || "trading_inventory"}
            onValueChange={(v) => patchOrg({ industry_type: v }, `Industry set to ${v}`)}
            disabled={busy}
          >
            <SelectTrigger className="rounded-sm mt-1" data-testid="industry-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {INDUSTRIES.map((i) => <SelectItem key={i.v} value={i.v}>{i.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-[10px] text-muted-foreground mt-1">
            Any <b>mfg_*</b> industry unlocks BOM · MRP · Production Scheduler · Machine Maintenance · Costing · Quality · NPD.
          </div>
        </div>
        <div className="space-y-2">
          <div>
            <Label>Manufacturing add-on</Label>
            <div className={`mt-1 px-3 py-2 rounded-sm border flex items-center gap-2 ${isMfg ? "border-emerald-300 bg-emerald-50/40" : "border-muted bg-muted/30"}`}>
              <span className={`h-2 w-2 rounded-full ${isMfg ? "bg-emerald-500" : "bg-muted-foreground"}`}></span>
              <span className="text-xs">{isMfg ? "Active — driven by Industry Type above" : "Inactive — pick an mfg_* industry to enable"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Dealership toggle */}
      <div className="border-t pt-3">
        <label className="flex items-start gap-3 cursor-pointer" data-testid="dealership-toggle-label-legal">
          <input
            type="checkbox"
            checked={!!org.is_dealership}
            disabled={busy}
            onChange={(e) => patchOrg({ is_dealership: e.target.checked }, e.target.checked ? "Dealership 360 enabled" : "Dealership 360 disabled")}
            className="mt-1"
            data-testid="dealership-toggle-legal"
          />
          <div>
            <div className="text-sm font-medium">Dealership 360 add-on <span className="text-[10px] font-mono uppercase text-accent ml-1">{org.is_dealership ? "ACTIVE" : "OFF"}</span></div>
            <div className="text-[11px] text-muted-foreground">Unlocks Dealership · Van Sales · Distribution · Beat Plan modules with auto DMS, on-road pricing, RTO forms.</div>
          </div>
        </label>
      </div>

      {expandedModules && (
        <div className="rounded-sm border border-sky-200 bg-sky-50/40 p-3 text-xs" data-testid="expanded-modules-preview">
          <div className="text-[10px] font-mono uppercase text-sky-800 mb-1">Active modules ({expandedModules.length})</div>
          <div className="flex flex-wrap gap-1">
            {expandedModules.map((m) => (
              <span key={m} className="px-1.5 py-0.5 bg-white border border-sky-200 rounded-sm font-mono text-[10px]">{m}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BrandingSection({ org, set }) {
  const [busy, setBusy] = React.useState(false);
  const fileRef = React.useRef(null);
  const templates = [
    { id: "modern",  label: "Modern",  desc: "Clean · orange accent bar · minimal borders (current default)" },
    { id: "classic", label: "Classic", desc: "Traditional · wider borders · title flanked by tildes" },
    { id: "compact", label: "Compact", desc: "Dense · smaller fonts · maximum info per page" },
    { id: "minimal", label: "Minimal", desc: "No accent bar · pure typography · executive summary style" },
  ];
  const fonts = [
    { id: "Helvetica",   label: "Helvetica",   preview: "Aa Bb Cc 0123 ₹ ABC" },
    { id: "Times-Roman", label: "Times Roman", preview: "Aa Bb Cc 0123 ₹ ABC" },
    { id: "Courier",     label: "Courier",     preview: "Aa Bb Cc 0123 ₹ ABC" },
  ];
  const onLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const b64 = await compressLogo(f);
      set({ logo_base64: b64 });
      await api.patch("/organization", { logo_base64: b64 });
      toast.success("Logo uploaded — every PDF will now include it");
    } catch (err) { toast.error(err?.response?.data?.detail || err?.message || "Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const clearLogo = async () => {
    await api.patch("/organization", { logo_base64: "" });
    set({ logo_base64: "" });
    toast.success("Logo removed");
  };
  const saveBrand = async (patch) => {
    set(patch);
    await api.patch("/organization", patch);
    toast.success("Branding updated");
  };
  return (
    <div className="space-y-4" data-testid="branding-section">
      <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Branding · applied to every PDF (invoice, PO, credit note, statement)</div>

      {/* Logo */}
      <div className="grid grid-cols-[100px,1fr] gap-3 items-start">
        <div className="w-24 h-24 border-2 border-dashed border-muted rounded-sm flex items-center justify-center bg-muted/30 overflow-hidden" data-testid="logo-preview">
          {org.logo_base64 ? (
            <img src={org.logo_base64} alt="logo" className="max-w-full max-h-full object-contain" />
          ) : (
            <span className="text-[9px] text-muted-foreground">No logo</span>
          )}
        </div>
        <div className="space-y-2">
          <Label>Company Logo</Label>
          <div className="text-[10px] text-muted-foreground">PNG, JPG or WebP · any size (large images are shrunk automatically). Displayed on the top-left of every PDF.</div>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogo} className="hidden" data-testid="logo-file" />
            <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} className="rounded-sm" data-testid="logo-upload-btn">
              {busy ? "Uploading…" : "Upload logo"}
            </Button>
            {org.logo_base64 && <Button size="sm" variant="ghost" onClick={clearLogo} className="rounded-sm text-red-600">Remove</Button>}
          </div>
        </div>
      </div>

      {/* Font */}
      <div>
        <Label>PDF Font</Label>
        <div className="grid grid-cols-3 gap-2 mt-1" data-testid="font-picker">
          {fonts.map((f) => (
            <button key={f.id} onClick={() => saveBrand({ pdf_font: f.id })}
              className={`text-left border rounded-sm p-3 transition ${org.pdf_font === f.id ? "border-accent bg-accent/10 shadow-sm" : "border-muted hover:border-accent/40"}`}
              data-testid={`font-${f.id}`}>
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{f.label}</div>
              <div className="text-lg mt-1" style={{ fontFamily: f.id === "Times-Roman" ? "'Times New Roman', serif" : f.id === "Courier" ? "'Courier New', monospace" : "Helvetica, Arial, sans-serif" }}>
                {f.preview}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Brand Color */}
      <div className="flex items-center gap-3">
        <div>
          <Label>Brand accent color</Label>
          <div className="flex gap-2 items-center mt-1">
            <input type="color" value={org.brand_color || "#EA580C"} onChange={(e) => saveBrand({ brand_color: e.target.value })} className="h-10 w-16 border rounded-sm" data-testid="brand-color-input" />
            <span className="font-mono text-xs">{org.brand_color || "#EA580C"}</span>
          </div>
        </div>
      </div>

      {/* Invoice Templates */}
      <div>
        <Label>Invoice Template</Label>
        <div className="grid grid-cols-4 gap-2 mt-1" data-testid="template-picker">
          {templates.map((t) => (
            <button key={t.id} onClick={() => saveBrand({ invoice_template: t.id })}
              className={`text-left border rounded-sm p-3 transition ${(org.invoice_template || "modern") === t.id ? "border-accent bg-accent/10 shadow-sm" : "border-muted hover:border-accent/40"}`}
              data-testid={`template-${t.id}`}>
              <div className="text-[10px] font-mono uppercase text-accent">{t.label}</div>
              <div className="text-[10px] text-muted-foreground mt-1 leading-tight">{t.desc}</div>
              <div className="mt-2 h-16 bg-white border rounded-sm p-1.5 text-[6px] leading-tight" style={{ borderColor: org.brand_color || "#EA580C" }}>
                <div className="font-bold" style={{ fontFamily: (org.pdf_font === "Times-Roman") ? "serif" : (org.pdf_font === "Courier") ? "monospace" : "sans-serif" }}>
                  {t.id === "classic" ? "~ TAX INVOICE ~" : "TAX INVOICE"}
                </div>
                {t.id !== "minimal" && <div className="h-0.5 mt-1" style={{ background: org.brand_color || "#EA580C" }} />}
                <div className="mt-1 text-[5px] opacity-60">Supplier · GSTIN · Line items…</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Dealership toggle */}
      <div className="border-t pt-3">
        <label className="flex items-center gap-3 cursor-pointer" data-testid="dealership-toggle-label">
          <input type="checkbox" checked={!!org.is_dealership} onChange={(e) => saveBrand({ is_dealership: e.target.checked })} data-testid="dealership-toggle" />
          <div>
            <div className="text-sm font-medium">This is a Dealership business</div>
            <div className="text-[10px] text-muted-foreground">Activates Dealership 360 features (bookings, dispatch, RTO). Can be enabled at company creation too.</div>
          </div>
        </label>
      </div>

      {/* Bank Details for the invoice footer */}
      <div className="border-t pt-4" data-testid="bank-details-section">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-2">Bank Details · printed on invoice footer for customer payment</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Bank Name</Label><Input value={org.bank_name || ""} onChange={(e) => set({ bank_name: e.target.value })} onBlur={() => saveBrand({ bank_name: org.bank_name })} className="mt-1" data-testid="bank-name" placeholder="HDFC Bank" /></div>
          <div><Label>Account Holder Name</Label><Input value={org.bank_account_name || ""} onChange={(e) => set({ bank_account_name: e.target.value })} onBlur={() => saveBrand({ bank_account_name: org.bank_account_name })} className="mt-1" data-testid="bank-account-name" placeholder={org.name} /></div>
          <div><Label>Account Number</Label><Input value={org.bank_account_number || ""} onChange={(e) => set({ bank_account_number: e.target.value })} onBlur={() => saveBrand({ bank_account_number: org.bank_account_number })} className="mt-1 font-mono" data-testid="bank-account-number" placeholder="50100123456789" /></div>
          <div><Label>IFSC Code</Label><Input value={org.bank_ifsc || ""} onChange={(e) => set({ bank_ifsc: (e.target.value || "").toUpperCase() })} onBlur={() => saveBrand({ bank_ifsc: (org.bank_ifsc || "").toUpperCase() })} className="mt-1 font-mono" data-testid="bank-ifsc" placeholder="HDFC0001234" /></div>
          <div><Label>Branch</Label><Input value={org.bank_branch || ""} onChange={(e) => set({ bank_branch: e.target.value })} onBlur={() => saveBrand({ bank_branch: org.bank_branch })} className="mt-1" data-testid="bank-branch" placeholder="Andheri West, Mumbai" /></div>
          <div><Label>UPI ID (optional)</Label><Input value={org.bank_upi || ""} onChange={(e) => set({ bank_upi: e.target.value })} onBlur={() => saveBrand({ bank_upi: org.bank_upi })} className="mt-1 font-mono" data-testid="bank-upi" placeholder="company@hdfc" /></div>
        </div>
      </div>

      {/* Authorised Signatory */}
      <div className="border-t pt-4" data-testid="signatory-section">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-2">Authorised Signatory · signature block on the invoice</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Full Name</Label><Input value={org.authorised_signatory_name || ""} onChange={(e) => set({ authorised_signatory_name: e.target.value })} onBlur={() => saveBrand({ authorised_signatory_name: org.authorised_signatory_name })} className="mt-1" data-testid="sig-name" placeholder="Rahul Sharma" /></div>
          <div><Label>Designation</Label><Input value={org.authorised_signatory_designation || ""} onChange={(e) => set({ authorised_signatory_designation: e.target.value })} onBlur={() => saveBrand({ authorised_signatory_designation: org.authorised_signatory_designation })} className="mt-1" data-testid="sig-designation" placeholder="Director / CFO" /></div>
        </div>
        <div className="mt-3">
          <Label>Invoice Footer Note (optional)</Label>
          <Input value={org.invoice_footer_note || ""} onChange={(e) => set({ invoice_footer_note: e.target.value })} onBlur={() => saveBrand({ invoice_footer_note: org.invoice_footer_note })} className="mt-1" data-testid="footer-note" placeholder="Subject to Mumbai jurisdiction. E&OE." />
        </div>
      </div>
      {/* Auto Payment Reminder (scheduler) */}
      <div className="border-t pt-4" data-testid="auto-reminder-section">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-2">Auto Payment Reminder · scheduled emails for overdue invoices</div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={!!org.auto_reminder_enabled} onChange={(e) => saveBrand({ auto_reminder_enabled: e.target.checked })} data-testid="auto-rem-toggle" />
          <div>
            <div className="text-sm font-medium">Send reminders automatically</div>
            <div className="text-[10px] text-muted-foreground">Attaches the tax invoice PDF and emails the customer once at each milestone below.</div>
          </div>
        </label>
        {org.auto_reminder_enabled && (
          <div className="mt-3">
            <Label>Milestones (days past due, comma-separated)</Label>
            <Input
              value={(org.auto_reminder_days || [7, 15, 30]).join(", ")}
              onChange={(e) => {
                const days = e.target.value.split(",").map((s) => Number(s.trim())).filter((n) => n > 0);
                set({ auto_reminder_days: days });
              }}
              onBlur={() => saveBrand({ auto_reminder_days: org.auto_reminder_days })}
              className="rounded-sm mt-1 font-mono"
              data-testid="auto-rem-days"
              placeholder="7, 15, 30"
            />
            <div className="text-[10px] text-muted-foreground mt-1">The scheduler runs every 6 h. Each invoice + milestone is emailed exactly once.</div>
            <Button size="sm" variant="outline" className="rounded-sm mt-2" data-testid="auto-rem-run-now"
              onClick={async () => {
                try {
                  const r = await api.post("/sales/invoices/auto-reminders/run-now");
                  toast.success(`Scan complete · ${r.data.sent}/${r.data.scanned} reminders fired`);
                } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
              }}>Run scan now</Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Settings() {
  const [branches, setBranches] = useState([]);
  const [ccs, setCCs] = useState([]);
  const [locks, setLocks] = useState([]);
  const [companyGstins, setCompanyGstins] = useState([]);
  const [entityType, setEntityType] = useState("private_limited");

  const [nb, setNB] = useState({ name: "", code: "", state: "", gstin: "" });
  const [ncc, setNcc] = useState({ code: "", name: "", parent_id: "" });
  const [nl, setNL] = useState({ from_date: today(), to_date: today(), module: "all" });

  // Iter-190 · Dimension Merge dialog state
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSrc, setMergeSrc] = useState("");
  // Iter-190 · Dimension Split dialog state + caches
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitSrc, setSplitSrc] = useState("");
  const [ccAccounts, setCcAccounts] = useState([]);
  const [ccParties, setCcParties] = useState([]);

  const loadSplitDeps = async () => {
    try {
      const [a, p] = await Promise.all([api.get("/accounts"), api.get("/parties")]);
      setCcAccounts(a.data || []); setCcParties(p.data || []);
    } catch { /* silent — pickers just render empty */ }
  };

  const load = async () => {
    setBranches((await api.get("/branches")).data);
    setCCs((await api.get("/dimensions")).data);
    setLocks((await api.get("/period-locks")).data);
    try {
      const me = await api.get("/auth/me");
      setEntityType(me.data?.org?.entity_type || "private_limited");
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const createBranch = async () => {
    try { await api.post("/branches", nb); toast.success("Branch added"); setNB({ name: "", code: "", state: "", gstin: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const createCC = async () => {
    try { await api.post("/dimensions", { ...ncc, parent_id: ncc.parent_id || null });
      toast.success("Dimension added"); setNcc({ code: "", name: "", parent_id: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deleteCC = async (c) => {
    if (!window.confirm(`Delete "${c.name}" and any children?`)) return;
    try { await api.delete(`/dimensions/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const createLock = async () => {
    try { await api.post("/period-locks", nl); toast.success("Period locked"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const unlock = async (id) => {
    try { await api.post(`/period-locks/${id}/unlock`, { reason: "Manual unlock" }); toast.success("Unlocked"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="settings-page">
      <PageHeader eyebrow="Setup" title="Settings" description="Branches, dimensions, segments, voucher numbering, GST config, and period locking." />

      <Tabs defaultValue="company">
        <TabsList className="rounded-sm flex-wrap h-auto">
          <TabsTrigger value="company" className="rounded-sm" data-testid="tab-company">Company</TabsTrigger>
          <TabsTrigger value="branches" className="rounded-sm">Branches</TabsTrigger>
          <TabsTrigger value="departments" className="rounded-sm" data-testid="tab-departments">Departments</TabsTrigger>
          <TabsTrigger value="cc" className="rounded-sm" data-testid="tab-dimensions">Dimensions</TabsTrigger>
          <TabsTrigger value="partners" className="rounded-sm" data-testid="tab-partners">Partners</TabsTrigger>
          <TabsTrigger value="shareholders" className="rounded-sm" data-testid="tab-shareholders">Shareholders</TabsTrigger>
          <TabsTrigger value="esign" className="rounded-sm" data-testid="tab-esign-signatories">eSign Signatories</TabsTrigger>
          <TabsTrigger value="statement-format" className="rounded-sm" data-testid="tab-statement-format">Statement Format</TabsTrigger>
          <TabsTrigger value="doctypes" className="rounded-sm" data-testid="tab-doctypes">Document Config</TabsTrigger>
          <TabsTrigger value="numbering" className="rounded-sm" data-testid="tab-numbering">Voucher Numbering</TabsTrigger>
          <TabsTrigger value="approval_rules" className="rounded-sm" data-testid="tab-approval-rules">Approvals</TabsTrigger>
          <TabsTrigger value="roles" className="rounded-sm" data-testid="tab-roles">User Roles</TabsTrigger>
          <TabsTrigger value="permissions" className="rounded-sm" data-testid="tab-permissions">Permissions</TabsTrigger>
          <TabsTrigger value="activity" className="rounded-sm" data-testid="tab-activity">Activity</TabsTrigger>
          <TabsTrigger value="gst" className="rounded-sm" data-testid="tab-gst-settings">GST Config</TabsTrigger>
          <TabsTrigger value="compliance" className="rounded-sm" data-testid="tab-compliance">E-Invoice / EWB</TabsTrigger>
          <TabsTrigger value="master" className="rounded-sm" data-testid="tab-master-data">Master Data</TabsTrigger>
          <TabsTrigger value="opening" className="rounded-sm" data-testid="tab-opening-balances">Opening Balances</TabsTrigger>
          <TabsTrigger value="fa" className="rounded-sm" data-testid="tab-fixed-assets">Fixed Assets</TabsTrigger>
          <TabsTrigger value="bank_memory" className="rounded-sm" data-testid="tab-bank-memory">Bank Memory</TabsTrigger>
          <TabsTrigger value="cheque_books" className="rounded-sm" data-testid="tab-cheque-books">Cheque Book</TabsTrigger>
          <TabsTrigger value="workflow_console" className="rounded-sm" data-testid="tab-workflow-console">Workflow Console</TabsTrigger>
          <TabsTrigger value="locks" className="rounded-sm">Period Locks</TabsTrigger>
          <TabsTrigger value="integrations" className="rounded-sm" data-testid="tab-integrations">Integrations</TabsTrigger>
          <TabsTrigger value="usage" className="rounded-sm" data-testid="tab-usage">Usage</TabsTrigger>
          <TabsTrigger value="help" className="rounded-sm" data-testid="tab-help">Help Articles</TabsTrigger>
          <TabsTrigger value="trash" className="rounded-sm" data-testid="tab-trash">Trash &amp; Export</TabsTrigger>
          <TabsTrigger value="backup" className="rounded-sm" data-testid="tab-backup">Backup</TabsTrigger>
          <TabsTrigger value="referrals" className="rounded-sm" data-testid="tab-referrals">Refer &amp; Earn</TabsTrigger>
          <TabsTrigger value="password" className="rounded-sm" data-testid="tab-password">Password</TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="mt-4">
          <CompanyProfileTab />
        </TabsContent>

        <TabsContent value="branches" className="mt-4 space-y-4">
          <div className="card-sharp p-3">
            <DimensionBulkUpload kind="branches" label="Branches" onDone={load} />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
          <div className="card-sharp p-5 md:col-span-2">
            <table className="w-full dense">
              <thead><tr><th>Code</th><th>Name</th><th>State</th><th>GSTIN</th></tr></thead>
              <tbody>{branches.map((b) => (
                <tr key={b.id}><td className="font-mono text-xs">{b.code}</td><td>{b.name} {b.is_head_office && <span className="text-[10px] font-mono uppercase text-accent ml-1">HO</span>}</td><td className="text-xs">{b.state || "—"}</td><td className="font-mono text-xs">{b.gstin || "—"}</td></tr>
              ))}</tbody>
            </table>
          </div>
          <div className="card-sharp p-5 space-y-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">New Branch</div>
            <div><Label>Name</Label><Input value={nb.name} onChange={(e) => setNB({ ...nb, name: e.target.value })} className="rounded-sm mt-1" data-testid="new-branch-name" /></div>
            <div><Label>Code</Label><Input value={nb.code} onChange={(e) => setNB({ ...nb, code: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>State</Label><Input value={nb.state} onChange={(e) => setNB({ ...nb, state: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>GSTIN</Label>
              <select value={nb.gstin || ""} onChange={(e) => setNB({ ...nb, gstin: e.target.value })}
                        className="w-full mt-1 border rounded-sm px-2 py-2 bg-background font-mono text-sm"
                        data-testid="new-branch-gstin">
                <option value="">— None —</option>
                {(branches || []).length === 0 && companyGstins.length === 0 && (
                  <option disabled>Add GSTINs at Settings → Company GSTINs first</option>
                )}
                {companyGstins.map((g) => (
                  <option key={g.gstin} value={g.gstin}>{g.gstin} · {g.label}</option>
                ))}
              </select>
              <p className="text-[10px] text-muted-foreground mt-1">
                Pick from company GSTINs. Not listed? Add it in Company GSTINs.
              </p>
            </div>
            <Button onClick={createBranch} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white">Add branch</Button>
          </div>
          </div>
        </TabsContent>

        <TabsContent value="cc" className="mt-4 space-y-4">
          <div className="card-sharp p-3">
            <DimensionBulkUpload kind="cost-centres" label="Dimensions" onDone={load} />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
          <div className="card-sharp p-5 md:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Dimension hierarchy</div>
              {ccs.length >= 2 && (
                <Button size="sm" variant="outline" onClick={() => setMergeOpen(true)}
                        className="rounded-sm h-7 text-xs gap-1"
                        data-testid="cc-merge-btn">
                  Merge duplicates
                </Button>
              )}
            </div>
            {ccs.length === 0
              ? <div className="text-sm text-muted-foreground py-6 text-center">No dimensions yet</div>
              : <table className="w-full dense" data-testid="cc-table">
                  <thead><tr><th>Code</th><th>Name</th><th>Parent</th><th className="w-24"></th></tr></thead>
                  <tbody>{ccs.map((c) => (
                    <tr key={c.id} className="border-t border-border/60 hover:bg-muted/30" data-testid={`cc-row-${c.id}`}>
                      <td className="font-mono text-xs">{c.code}</td>
                      <td>{c.name}{c.tally_is_category && <span className="text-[9px] font-mono uppercase text-orange-600 ml-2 px-1 py-0.5 rounded-sm bg-orange-100">Category</span>}</td>
                      <td className="text-xs text-muted-foreground">{(ccs.find(x => x.id === c.parent_id) || {}).name || "—"}</td>
                      <td className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => { setSplitSrc(c.id); loadSplitDeps(); setSplitOpen(true); }}
                                className="h-7 text-xs mr-1 text-blue-600"
                                data-testid={`cc-split-${c.id}`}>Split</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setMergeSrc(c.id); setMergeOpen(true); }}
                                className="h-7 text-xs mr-1"
                                data-testid={`cc-merge-${c.id}`}>Merge</Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteCC(c)}
                                className="h-7 text-xs text-red-600" data-testid={`cc-delete-${c.id}`}>Delete</Button>
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
            }
          </div>
          <div className="card-sharp p-5 space-y-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">New Dimension</div>
            <div><Label>Code</Label><Input value={ncc.code} onChange={(e) => setNcc({ ...ncc, code: e.target.value })} className="rounded-sm mt-1" data-testid="new-cc-code" /></div>
            <div><Label>Name</Label><Input value={ncc.name} onChange={(e) => setNcc({ ...ncc, name: e.target.value })} className="rounded-sm mt-1" data-testid="new-cc-name" /></div>
            <div>
              <Label>Parent (optional)</Label>
              <Select value={ncc.parent_id || "none"} onValueChange={(v) => setNcc({ ...ncc, parent_id: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="new-cc-parent"><SelectValue placeholder="None (top-level)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (top-level)</SelectItem>
                  {ccs.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} · {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={createCC} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="add-cc-btn">Add</Button>
          </div>

          <MergeCcDialog open={mergeOpen} onOpenChange={setMergeOpen} ccs={ccs}
                          initialSrc={mergeSrc}
                          onDone={() => { setMergeOpen(false); setMergeSrc(""); load(); }} />
          <SplitCcDialog open={splitOpen} onOpenChange={setSplitOpen}
                          ccs={ccs} accounts={ccAccounts} parties={ccParties}
                          initialSrc={splitSrc}
                          onDone={() => { setSplitOpen(false); setSplitSrc(""); load(); }} />
          </div>
        </TabsContent>

        <TabsContent value="locks" className="mt-4 grid md:grid-cols-3 gap-4">
          <div className="card-sharp overflow-hidden md:col-span-2">
            <table className="w-full dense">
              <thead><tr><th>From</th><th>To</th><th>Module</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {locks.length === 0 && <tr><td colSpan="5" className="text-center py-8 text-muted-foreground">No locks yet</td></tr>}
                {locks.map((l) => (
                  <tr key={l.id}>
                    <td className="text-xs">{fmtDate(l.from_date)}</td>
                    <td className="text-xs">{fmtDate(l.to_date)}</td>
                    <td className="text-xs uppercase font-mono">{l.module}</td>
                    <td>{l.unlocked_at ? <span className="text-[10px] font-mono uppercase text-muted-foreground">Unlocked</span> : <span className="text-[10px] font-mono uppercase text-accent flex items-center gap-1"><Lock className="w-3 h-3" /> Locked</span>}</td>
                    <td className="text-right">{!l.unlocked_at && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => unlock(l.id)}><Unlock className="w-3.5 h-3.5 mr-1" /> Unlock</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card-sharp p-5 space-y-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Lock a period</div>
            <div><Label>From</Label><Input type="date" value={nl.from_date} onChange={(e) => setNL({ ...nl, from_date: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>To</Label><Input type="date" value={nl.to_date} onChange={(e) => setNL({ ...nl, to_date: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>Module</Label>
              <Select value={nl.module} onValueChange={(v) => setNL({ ...nl, module: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All modules</SelectItem>
                  <SelectItem value="accounting">Accounting only</SelectItem>
                  <SelectItem value="sales">Sales only</SelectItem>
                  <SelectItem value="purchase">Purchase only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={createLock} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="create-lock-btn"><Lock className="w-3.5 h-3.5" /> Lock period</Button>
          </div>
        </TabsContent>

        <TabsContent value="numbering" className="mt-4">
          <VoucherNumbering />
        </TabsContent>

        <TabsContent value="shareholders" className="mt-4">
          <ShareholdersTab />
        </TabsContent>

        <TabsContent value="esign" className="mt-4">
          <ESignSignatoriesTab />
        </TabsContent>

        <TabsContent value="partners" className="mt-4">
          <PartnersTab entityType={entityType} />
        </TabsContent>

        <TabsContent value="statement-format" className="mt-4">
          <StatementFormatEditor />
        </TabsContent>

        <TabsContent value="gst" className="mt-4">
          <GstConfig />
        </TabsContent>

        <TabsContent value="master" className="mt-4">
          <MasterDataTab />
        </TabsContent>

        <TabsContent value="opening" className="mt-4">
          <OpeningBalancesWizard />
        </TabsContent>

        <TabsContent value="fa" className="mt-4">
          <FixedAssetsWizard />
        </TabsContent>

        <TabsContent value="roles" className="mt-4 space-y-5">
          <UserManagement />
          <RolesMatrix />
        </TabsContent>

        <TabsContent value="permissions" className="mt-4">
          <PermissionGrid />
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <ActivityTimeline />
        </TabsContent>

        <TabsContent value="usage" className="mt-4">
          <UsageMetricsTab />
        </TabsContent>

        <TabsContent value="integrations" className="mt-4 space-y-5">
          <GstBridgePanel />
          <SmtpSettingsPanel />
          <EmailSignaturePanel />
          <EmailOutboxPanel />
          <HRSyncPanel />
        </TabsContent>

        <TabsContent value="compliance" className="mt-4">
          <ComplianceTab />
        </TabsContent>

        <TabsContent value="approval_rules" className="mt-4">
          <ApprovalRulesTab />
        </TabsContent>

        <TabsContent value="departments" className="mt-4">
          <DepartmentsTab />
        </TabsContent>

        <TabsContent value="doctypes" className="mt-4">
          <DocumentTypesTab />
        </TabsContent>

        <TabsContent value="help" className="mt-4">
          <HelpArticleEditor />
        </TabsContent>

        <TabsContent value="trash" className="mt-4">
          <TrashRestore />
        </TabsContent>

        <TabsContent value="backup" className="mt-4">
          <BackupTab />
        </TabsContent>

        <TabsContent value="referrals" className="mt-4">
          <Referrals />
        </TabsContent>

        <TabsContent value="password" className="mt-4">
          <PasswordTab />
        </TabsContent>

        <TabsContent value="bank_memory" className="mt-4">
          <BankMemoryTab />
        </TabsContent>

        <TabsContent value="cheque_books" className="mt-4">
          <ChequeBookTab />
        </TabsContent>

        <TabsContent value="workflow_console" className="mt-4">
          <WorkflowConsoleTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
